/**
 * x-row-window="10": a scroll box that shows the first N rows it holds marked `data-window-row`, under
 * its sticky header, and scrolls for the rest (Ola's QA, 9 Oct: an opened topic of What brings people
 * here listed every one of its searches, hundreds for some, down the page).
 *
 * Its height is measured off those rows rather than set in rem: a search row is one line or three (its
 * top page, then Draft an article) depending on the search and on what the reader may do. Only the
 * marked rows count, so the rows between them (a search's pages) scroll inside the same height. With N
 * rows or fewer nothing is capped and nothing scrolls, but for a share of the window (below).
 *
 * It measures in the next frame, never in the observer's callback, as x-fit-rows does and for its
 * reason, on any change of size of the box or of its table: rows arriving, a search narrowing them, a
 * reflow at another width. A box with no layout (a topic folded away, Stats hidden) measures nothing and
 * keeps the height it had.
 *
 * x-row-window.vh.70="15" also never grows past that share of the window's height, however few rows it
 * holds: an activity table's rows wrap their chips onto two or three lines, so even a short list of them
 * can stand taller than a laptop's screen and push the controls round the box out of sight (Ola's QA
 * walk, 9 Oct).
 *
 * Convention exception: inline style. The cap is a height measured in the browser from the rows on
 * screen, the "truly dynamic value computed at runtime" the no-inline-styles rule carves out.
 */
export default function RowWindowDirective(el, { expression, modifiers = [] }, { cleanup }) {
    const count = Math.max(1, Number.parseInt(expression, 10) || 10);
    const share = modifiers.includes('vh') ? Number.parseInt(modifiers[modifiers.indexOf('vh') + 1], 10) || null : null;
    let frame = null;

    const fit = () => {
        const rows = [...el.querySelectorAll('[data-window-row]')];

        if (rows.length <= count) {
            el.style.maxHeight = share === null ? '' : `${share}vh`;

            return;
        }

        const height = windowHeight(el, rows.slice(0, count));

        if (height > 0) el.style.maxHeight = share === null ? `${Math.ceil(height)}px` : `min(${Math.ceil(height)}px, ${share}vh)`;
    };

    const observer = new ResizeObserver(() => {
        if (frame !== null) return;

        frame = window.requestAnimationFrame(() => {
            frame = null;
            fit();
        });
    });

    observer.observe(el);
    [...el.children].forEach((child) => observer.observe(child));
    cleanup(() => {
        observer.disconnect();

        if (frame !== null) window.cancelAnimationFrame(frame);
    });
}

/** The height that shows these rows from the box's top: its sticky header, then the rows themselves. */
export function windowHeight(el, rows) {
    const header = el.querySelector('thead');

    return (header?.getBoundingClientRect().height ?? 0) + rows.reduce((sum, row) => sum + row.getBoundingClientRect().height, 0);
}
